/**
 * Integrytas Group - Global Search Indexer (GSI)
 * use_global_search - debounced hook over the gsi_search RPC.
 *
 * The gateway calls gsi_search under the user's own JWT, which filters every
 * hit to the active workspace + the caller's role-page-access (admins see all).
 * Results are already permission-safe; the UI just renders them.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiDb } from '../lib/api_client';
import { asfWorkspaceCode } from '../lib/asf';

export interface GsiHit {
  entity_type: string;
  record_pk: string;
  title: string | null;
  une_workspace_id: number | null;
  rank: number;
}

export interface UseGlobalSearch {
  query: string;
  setQuery: (q: string) => void;
  hits: GsiHit[];
  busy: boolean;
  error: string | null;
  run: (q?: string) => Promise<void>;
}

export function useGlobalSearch(opts?: { debounceMs?: number; limit?: number; workspaceCode?: string | null }): UseGlobalSearch {
  const debounceMs = opts?.debounceMs ?? 250;
  const limit = opts?.limit ?? 30;
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<GsiHit[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  const run = useCallback(async (q?: string) => {
    const term = (q ?? query).trim();
    const mine = ++seq.current;
    if (!term) { setHits([]); setError(null); setBusy(false); return; }
    setBusy(true); setError(null);
    try {
      const ws = opts?.workspaceCode !== undefined ? opts.workspaceCode : (asfWorkspaceCode() || null);
      const { data, error: e } = await apiDb.rpc<GsiHit[]>('gsi_search', { p_query: term, p_workspace_code: ws, p_limit: limit });
      if (e) throw e;
      if (mine === seq.current) setHits(Array.isArray(data) ? data : []);
    } catch (err) {
      if (mine === seq.current) { setError((err as Error).message); setHits([]); }
    } finally {
      if (mine === seq.current) setBusy(false);
    }
  }, [query, limit, opts?.workspaceCode]);

  useEffect(() => {
    const term = query.trim();
    if (!term) { setHits([]); setBusy(false); return; }
    const h = setTimeout(() => { void run(term); }, debounceMs);
    return () => clearTimeout(h);
  }, [query, debounceMs, run]);

  return { query, setQuery, hits, busy, error, run };
}
